import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OnPowerLogo from '../shared/components/brand/OnPowerLogo.jsx';
import OnPowerLogoIcon from '../shared/components/brand/OnPowerLogoIcon.jsx';
import 'leaflet/dist/leaflet.css';
import TaskFinalizationForm from '../features/tasks/components/tasksForms/TaskFinalizationForm.jsx';
import TaskGeneralForm from '../features/tasks/components/tasksForms/TaskGeneralForm.jsx';
import TaskCancellationForm from '../features/tasks/components/tasksForms/TaskCancellationForm.jsx';
import ConfirmDialog from '../shared/components/ui/ConfirmDialog.jsx';
import { List } from '@phosphor-icons/react';
import { clearAuth, getAuthToken } from '../context/authContext';
import Alerts from '../shared/components/ui/Alerts.jsx';
import * as technicianService from '../services/technicianService';

// Componentes hijos extraídos
import TaskStats from '../features/technician/components/TaskStats.jsx';
import TaskCard from '../features/technician/components/TaskCard.jsx';
import TaskMap from '../features/technician/components/TaskMap.jsx';
import AddressModal from '../features/technician/components/modals/AddressModal.jsx';
import DynamicFormModal from '../features/technician/components/modals/DynamicFormModal.jsx';
import useFormModal from '../hooks/useFormModal';
import useAlert from '../hooks/useAlert';
import SideNav from '../features/technician/components/SideNav.jsx';
import ActiveUsersView from '../features/audit/screens/ActiveUsersView.jsx';
import TaskHistoryView from '../features/technician/screens/TaskHistoryView.jsx';
import CompletedDayCard from '../features/technician/components/CompletedDayCard.jsx';
import { AuthContext } from '../context/authContext.jsx';
import { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { useGuidedTour, useGuidedTourStep } from '../shared/hooks/useGuidedTour.js';
import TourButton from '../shared/components/ui/TourButton.jsx';
import TourFormPreview from '../features/technician/components/TourFormPreview.jsx';
import { technicianTour, TOUR_DEMO_TASK, TOUR_DEMO_FORMS } from '../features/technician/technicianTour.js';

const ACTIVE_TASK_STATUSES = ['en_camino', 'iniciada', 'relevada'];

const buildRouteTaskMetadata = (routePlans = []) => {
  const routeOrderByTaskId = new Map();
  const teamsWithConfirmedRoute = new Set();

  routePlans.forEach((plan) => {
    const teamId = String(plan?.teamId || '');
    const taskStops = (plan?.stops || [])
      .filter((stop) => stop?.type === 'TASK' && stop?.taskId && stop?.status !== 'finalizada')
      .sort((left, right) => Number(left.order || 0) - Number(right.order || 0));

    if (!teamId || !taskStops.length) {
      return;
    }

    teamsWithConfirmedRoute.add(teamId);
    const seenTaskIds = new Set();

    taskStops.forEach((stop) => {
      const taskId = String(stop.taskId);
      if (seenTaskIds.has(taskId)) {
        return;
      }

      seenTaskIds.add(taskId);
      routeOrderByTaskId.set(taskId, seenTaskIds.size);
    });
  });

  return {
    routeOrderByTaskId,
    teamsWithConfirmedRoute
  };
};

const TechnicianDashboard = () => {
  const FINAL_TASK_STATUSES = ['finalizada', 'cancelada'];

  // Estados principales
  const [tasks, setTasks] = useState([]);
  const [routePlans, setRoutePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLocationLinks, setShowLocationLinks] = useState({});
  const [taskTimers, setTaskTimers] = useState({});
  const [activeView, setActiveView] = useState('today'); // 'today' | 'history'
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const navigate = useNavigate();
  const { isAuthenticated, user } = useContext(AuthContext)

  // Estados de modales usando custom hooks
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [selectedTaskForAddress, setSelectedTaskForAddress] = useState(null);
  const dynamicModal = useFormModal();
  const finalizationModal = useFormModal();
  const generalModal = useFormModal();
  const cancellationModal = useFormModal();

  // Estado de alertas usando custom hook
  const alert = useAlert();

  // Recorrido guiado (technicianTour), solo en Tareas del Día. Mientras dura, la lista muestra una
  // tarea de ejemplo en el estado del paso y se abren sus ventanas de ejemplo (detalle, formularios):
  // sus acciones no hacen nada, así que avanzar no cambia estados ni envía formularios.
  // En los pasos del menú se abre el menú lateral (en móvil está oculto).
  const [tourStep, setTourStep] = useState(null);
  useGuidedTourStep((step) => {
    setTourStep(step);
    setIsMobileMenuOpen(Boolean(step?.sidebar));
  });
  const tour = useGuidedTour({ steps: activeView === 'today' ? technicianTour : undefined });
  const tourDemoTask = tourStep ? { ...TOUR_DEMO_TASK, status: tourStep.demoStatus || 'pendiente' } : null;
  const tourDemoForm = tourStep?.modal?.startsWith('form-') ? TOUR_DEMO_FORMS[tourStep.modal.replace('form-', '')] : null;
  const noop = () => {};


  // Cargar tareas
  const fetchTasks = async ({ background = false } = {}) => {
    try {
      if (!background) {
        setLoading(true);
      }
      const response = await technicianService.fetchTasks('today');
      const tasksResponse = Array.isArray(response?.tasks) ? response.tasks : [];
      setTasks(tasksResponse);
      setRoutePlans(Array.isArray(response?.routePlans) ? response.routePlans : []);
      if (!background) {
        setLoading(false);
      }
    } catch (err) {
      if (!background) {
        alert.show('Error', 'No se pudieron cargar las tareas asignadas. Por favor, recarga la pagina.');
        setRoutePlans([]);
        setLoading(false);
      }
    }
  };

  // Efecto inicial
  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    const isRecalculatingRoute = routePlans.some((plan) => (
      plan?.recalculationStatus === 'pending' || plan?.recalculationStatus === 'running'
    ));
    if (!isRecalculatingRoute) {
      return undefined;
    }

    const refreshInterval = setInterval(() => {
      fetchTasks({ background: true });
    }, 3000);

    return () => clearInterval(refreshInterval);
  }, [routePlans]);

  // Actualizar timers cada segundo
  useEffect(() => {
    const timerInterval = setInterval(() => {
      const activeTasks = tasks.filter(task => task.status === 'iniciada');
      if (activeTasks.length > 0) {
        setTaskTimers(prev => {
          const newTimers = { ...prev };
          activeTasks.forEach(task => {
            newTimers[task.id] = technicianService.getTaskElapsedTime(task.startedAt);
          });
          return newTimers;
        });
      }
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [tasks]);

  // Handlers de acciones
  const handleAction = async (id, action) => {
    // Si la acción es "en-camino", mostrar diálogo de confirmación
    if (action === 'en-camino') {
      setPendingAction({ id, action, task: tasks.find(t => t.id === id) });
      setShowConfirmDialog(true);
      return;
    }

    // Proceder con otras acciones normalmente
    await executeAction(id, action);
  };

  // Función para ejecutar la acción después de confirmación
  const executeAction = async (id, action) => {
    try {
      let endpoint;
      switch (action) {
        case 'en-camino':
          endpoint = 'en-camino';
          break;
        case 'start':
          // Inicio directo de tarea - verificación de formulario se hace en TaskCard
          endpoint = 'start';
          break;
        case 'finish':
          await technicianService.finishTask(id);
          fetchTasks({ background: true });
          setshowAlertsTitle('Tarea Finalizada');
          setshowAlertsContenido('Tarea finalizada correctamente');
          setshowAlerts(true);
          return;
        default:
          endpoint = action;
      }

      await technicianService.updateTaskStatus(id, endpoint);

      // Si es "en-camino", mostrar dirección y enviar notificación
      if (action === 'en-camino') {
        const task = tasks.find(t => t.id === id);
        if (task) {
          // Intentar enviar notificación, pero no bloquear si falla
          try {
            await technicianService.sendEnCaminoNotification(id);
          } catch (notifError) {
            console.warn('No se pudo enviar notificación en camino:', notifError);
            // Mostrar alerta al usuario pero continuar con el flujo
            alert.show('Advertencia', 'No se pudo enviar la notificación al cliente.');
          }
          setShowLocationLinks(prev => ({ ...prev, [id]: true }));
          setSelectedTaskForAddress(task);
        }
      }
      fetchTasks({ background: true });
    } catch (err) {
      console.error('Error al actualizar tarea:', err);
      // El servicio ya extrae el mensaje del backend y lo incluye en err.message
      alert.show('Error', err.message || 'No se pudo actualizar el estado de la tarea. Por favor, intenta nuevamente.');
    }
  };

  // Handlers para el diálogo de confirmación
  const handleConfirmAction = async () => {
    setShowConfirmDialog(false);
    if (pendingAction) {
      await executeAction(pendingAction.id, pendingAction.action);
      setPendingAction(null);
    }
  };

  const handleCancelAction = () => {
    setShowConfirmDialog(false);
    setPendingAction(null);
  };

  const handleOpenCancelModal = async (task) => {
    cancellationModal.open(task);
  };

  const handleOpenGeneralFormModal = async (task) => {
    try {
      const forms = await technicianService.fetchFormsByTaskType(task.taskTypeId, 'general');
      if (forms && forms.length > 0) {
        generalModal.open(task, forms[0]);
      } else {
        alert.show('No hay formularios', 'No hay formulario general para este tipo de tarea.');
      }
    } catch (err) {
      console.error('Error cargando formulario general:', err);
      alert.show('Error', 'Error al cargar formulario general');
    }
  };



  const handleOpenFormModal = async (task) => {
    try {
      // Cerrar otros modales si están abiertos
      if (finalizationModal.isOpen) {
        finalizationModal.close();
      }

      const forms = await technicianService.fetchFormsByTaskType(task.taskTypeId, 'finalizacion');
      if (forms && forms.length > 0) {
        dynamicModal.open(task, forms[0]);
      } else {
        alert.show('No hay formularios disponibles', 'No hay formularios disponibles para este tipo de actividad.');
      }
    } catch (err) {
      console.error('Error cargando formularios:', err);
      alert.show('Error', 'Error al cargar formularios disponibles');
    }
  };

  const handleFormSubmit = () => {
    dynamicModal.close();
    fetchTasks({ background: true });
    alert.show('Formulario completado', 'Formulario completado correctamente. Tarea finalizada');
  };

  const handleOpenFinalizationForm = (task) => {
    // Cerrar otros modales si están abiertos
    if (dynamicModal.isOpen) {
      dynamicModal.close();
    }
    finalizationModal.open(task);
  };

  const handleFinalizationSuccess = () => {
    finalizationModal.close();
    fetchTasks({ background: true });
    alert.show('Tarea Finalizada', 'Tarea finalizada correctamente');
  };

  const handleGeneralFormSuccess = () => {
    // Formulario general completado - tarea cambia a estado "relevada"
    generalModal.close();
    fetchTasks({ background: true });  // Refrescar para obtener estado actualizado
    // No mostrar alerta - solo refrescar
  };

  const handleCancellationSuccess = () => {
    cancellationModal.close();
    fetchTasks({ background: true });
    alert.show('Tarea Cancelada', 'Tarea cancelada correctamente');
  };

  const handleLogoutComplete = async () => {
    try {
      const token = getAuthToken();
      await fetch('/api/audit/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      clearAuth();
      navigate('/admin/login');
    }
  };

  const openGoogleMaps = (task) => {
    technicianService.openGoogleMaps(task.client?.lat, task.client?.lng, task.client?.address);
  };

  const handleOpenAddressModal = (task) => {
    setSelectedTaskForAddress(task);
    setShowAddressModal(true);
  };

  // Verificar si hay tarea activa
  const hasActiveTask = tasks.some((task) => ACTIVE_TASK_STATUSES.includes(task.status));
  const {
    routeOrderByTaskId,
    teamsWithConfirmedRoute
  } = buildRouteTaskMetadata(routePlans);
  const uniqueTasks = [...new Map(tasks.map((task) => [String(task.id), task])).values()];
  const displayTasks = uniqueTasks
    .sort((left, right) => {
      const leftOrder = routeOrderByTaskId.get(String(left.id));
      const rightOrder = routeOrderByTaskId.get(String(right.id));
      const leftHasRouteOrder = Number.isFinite(leftOrder);
      const rightHasRouteOrder = Number.isFinite(rightOrder);
      const sameTeam = String(left.teamId || '') === String(right.teamId || '');

      if (sameTeam && leftHasRouteOrder && rightHasRouteOrder && leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      if (sameTeam && leftHasRouteOrder !== rightHasRouteOrder) {
        return leftHasRouteOrder ? -1 : 1;
      }

      const leftScheduledAt = left.scheduledDate ? new Date(left.scheduledDate).getTime() : Number.POSITIVE_INFINITY;
      const rightScheduledAt = right.scheduledDate ? new Date(right.scheduledDate).getTime() : Number.POSITIVE_INFINITY;
      if (leftScheduledAt !== rightScheduledAt) {
        return leftScheduledAt - rightScheduledAt;
      }

      return String(left.title || '').localeCompare(String(right.title || ''));
    })
    .map((task, index) => {
      const teamId = String(task.teamId || '');
      const routeOrder = routeOrderByTaskId.get(String(task.id));
      const hasConfirmedRoute = teamsWithConfirmedRoute.has(teamId);

      if (Number.isFinite(routeOrder)) {
        return {
          ...task,
          number: routeOrder,
          isOutsideConfirmedRoute: false
        };
      }

      if (hasConfirmedRoute) {
        return {
          ...task,
          number: null,
          isOutsideConfirmedRoute: true
        };
      }

      return {
        ...task,
        number: index + 1,
        isOutsideConfirmedRoute: false
      };
    });
  const hasVisibleTasks = displayTasks.some(
    task => !FINAL_TASK_STATUSES.includes(task.status)
  );
  const allTasksAreFinal = displayTasks.length > 0 && !hasVisibleTasks;
  const completedTasksCount = displayTasks.filter(task => task.status === 'finalizada').length;
  const cancelledTasksCount = displayTasks.filter(task => task.status === 'cancelada').length;
  const technicianName = user?.name || '';

  // Centro por defecto del mapa
  const defaultCenter = { lat: -31.5375, lng: -68.5364 };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-content text-xl">Cargando tareas...</div>
      </div>
    );
  }




  if (!isAuthenticated) {
    return (
      <div className="container">
        <Navigate to="/admin/login" replace />
      </div>
    )
  }
  return (
    <div className="flex min-h-screen bg-bg">
      {/* Side Navigation */}
      <SideNav
        activeView={activeView}
        onViewChange={setActiveView}
        onLogout={handleLogoutComplete}
        isMobileOpen={isMobileMenuOpen}
        onMobileToggle={setIsMobileMenuOpen}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <div className="bg-surface border-b border-line sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-2 sm:space-x-3">
                {/* Hamburger Menu Button - Mobile Only */}
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="md:hidden p-2 text-content hover:text-content-muted transition-colors"
                  aria-label="Abrir menú"
                >
                  <List size={24} weight="bold" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Contenido principal */}
        <div className="flex-1 max-w-7xl w-full mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
          {activeView === 'today' && (
            <>
              {/* Estadísticas */}
              <div data-tour="tech-stats">
                <TaskStats tasks={tasks} />
              </div>

              {/* Lista de tareas */}
              {tourDemoTask ? (
                <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                  <TaskCard
                    task={tourDemoTask}
                    taskNumber={1}
                    taskTimer={754}
                    onAction={noop}
                    onOpenGoogleMaps={noop}
                    onOpenAddressModal={noop}
                    onOpenCancelModal={noop}
                    onOpenFormModal={noop}
                    onOpenGeneralFormModal={noop}
                    onOpenFinalizationForm={noop}
                    generalFormAvailable
                  />
                </div>
              ) : !hasVisibleTasks ? (
                allTasksAreFinal ? (
                  <CompletedDayCard
                    technicianName={technicianName}
                    completedTasksCount={completedTasksCount}
                    cancelledTasksCount={cancelledTasksCount}
                    totalTasksCount={displayTasks.length}
                  />
                ) : (
                  <div className="bg-surface rounded-xl shadow-md border border-line p-12 text-center">
                    <div className="text-6xl mb-4">📋</div>
                    <h3 className="text-lg font-semibold text-content mb-2">No tienes tareas asignadas</h3>
                    <p className="text-content">Cuando se te asignen tareas, aparecerán aqui.</p>
                  </div>
                )
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                  {displayTasks.map((task) => {
                    if (FINAL_TASK_STATUSES.includes(task.status)) {
                      return null;
                    }

                    return (
                      <TaskCard
                        key={task.id}
                        task={task}
                        taskNumber={task.number}
                        taskTimer={taskTimers[task.id] || 0}
                        showLocationLinks={showLocationLinks[task.id] || false}
                        onAction={handleAction}
                        onOpenGoogleMaps={openGoogleMaps}
                        onOpenAddressModal={handleOpenAddressModal}
                        onOpenCancelModal={handleOpenCancelModal}
                        onOpenFormModal={handleOpenFormModal}
                        onOpenGeneralFormModal={handleOpenGeneralFormModal}
                        onOpenFinalizationForm={handleOpenFinalizationForm}
                        hasActiveTask={hasActiveTask}
                        hasPendingForm={false}
                      />
                    );
                  })}
                </div>
              )}

              {/* Mapa de ubicaciones */}
              <div data-tour="tech-map">
                <TaskMap
                  tasks={displayTasks}
                  routePlans={routePlans}
                  defaultCenter={defaultCenter}
                />
              </div>
            </>
          )}

          {activeView === 'history' && (
            /* Vista de Historial */
            <TaskHistoryView />
          )}

          {activeView === 'users' && (
            /* Vista de Usuarios Activos */
            <ActiveUsersView />
          )}
        </div>
      </div>

      {/* Modales */}
      <AddressModal
        task={selectedTaskForAddress}
        isOpen={showAddressModal}
        onClose={() => {
          setShowAddressModal(false);
          setSelectedTaskForAddress(null);
        }}
        onOpenGoogleMaps={openGoogleMaps}
      />



      <DynamicFormModal
        task={dynamicModal.selectedTask}
        form={dynamicModal.selectedForm}
        isOpen={dynamicModal.isOpen}
        onClose={dynamicModal.close}
        onSubmit={handleFormSubmit}
      />

      {/* Formularios dinámicos existentes */}
      {finalizationModal.isOpen && finalizationModal.selectedTask && (
        <TaskFinalizationForm
          task={finalizationModal.selectedTask}
          onClose={finalizationModal.close}
          onSuccess={handleFinalizationSuccess}
        />
      )}

      {generalModal.isOpen && generalModal.selectedTask && (
        <TaskGeneralForm
          task={generalModal.selectedTask}
          onClose={generalModal.close}
          onSuccess={handleGeneralFormSuccess}
        />
      )}

      {cancellationModal.isOpen && cancellationModal.selectedTask && (
        <TaskCancellationForm
          task={cancellationModal.selectedTask}
          onClose={cancellationModal.close}
          onSuccess={handleCancellationSuccess}
        />
      )}

      {/* Ventanas de ejemplo del recorrido guiado */}
      {tourStep?.modal === 'details' && (
        <AddressModal task={tourDemoTask} isOpen onClose={noop} onOpenGoogleMaps={noop} />
      )}
      {tourDemoForm && <TourFormPreview key={tourStep.modal} form={tourDemoForm} />}

      {/* Diálogo de Confirmación */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="Confirmar acción"
        message={`¿Quieres marcar la tarea ${pendingAction?.task?.taskTypeId.replace('_', ' ')} de ${pendingAction?.task?.client?.name} como En camino? Esta acción notificará al cliente.`}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
        confirmText="Confirmar"
        cancelText="Cancelar"
      />

      {/* Alertas */}
      {alert.isOpen && (
        <Alerts
          title={alert.title}
          onClose={alert.close}
        >
          {alert.content}
        </Alerts>
      )}

      {tour.hasTour && <TourButton onClick={tour.start} />}
    </div>
  );
};

export default TechnicianDashboard;
